import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Trash2, Upload } from 'lucide-react';
import { listBrandAssets, createBrandAsset, updateBrandAsset, deleteBrandAsset } from './api';

const KINDS = [['logo', 'Logo'], ['palette', 'Palette'], ['style', 'Style reference'],
  ['template_page', 'Template page']];
const POSITIONS = [['bottom_right', 'Bottom right'], ['bottom_left', 'Bottom left'],
  ['top_right', 'Top right'], ['top_left', 'Top left'], ['center', 'Center']];

export default function BrandAssetsSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['training-brand-assets'],
    queryFn: listBrandAssets,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['training-brand-assets'] });

  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('logo');
  const [usageMode, setUsageMode] = useState('overlay');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);

  const create = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('label', label);
      fd.append('kind', kind);
      fd.append('usage_mode', usageMode);
      fd.append('notes', notes);
      fd.append('default_on', 'true');
      if (file) fd.append('image', file);
      return createBrandAsset(fd);
    },
    onSuccess: () => {
      toast.success('Brand asset added');
      setLabel(''); setNotes(''); setFile(null);
      invalidate();
    },
    onError: (e) => toast.error(e?.message || 'Could not add the asset'),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }) => updateBrandAsset(id, body),
    onSuccess: invalidate,
    onError: (e) => toast.error(e?.message || 'Update failed'),
  });

  const remove = useMutation({
    mutationFn: (id) => deleteBrandAsset(id),
    onSuccess: () => { toast.success('Removed'); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Delete failed'),
  });

  const assets = data?.brand_assets || [];
  const field = 'text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-transparent';

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Brand assets</h3>
        <p className="text-xs text-gray-500 mt-1 max-w-xl">
          Logos and style references that attach to generated artifact images. Authors can detach
          one for a single image without changing this list. Overlay assets are stamped onto the
          finished image exactly as uploaded. Reference assets guide the model instead, so use them
          for palette and style, not for a logo you need reproduced precisely.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
        <div className="flex flex-wrap gap-2">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label"
            className={`${field} flex-1 min-w-[140px]`} />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={field}>
            {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={usageMode} onChange={(e) => setUsageMode(e.target.value)} className={field}>
            <option value="overlay">Overlay (stamped exactly)</option>
            <option value="reference">Reference (guides the model)</option>
          </select>
          <label className={`${field} inline-flex items-center gap-1 cursor-pointer`}>
            <Upload size={12} /> {file ? file.name : 'Choose image'}
            <input type="file" accept="image/png,image/jpeg" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <input value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Guidance sent with the prompt, e.g. never recolor, keep clear space"
          className={`${field} w-full`} />
        <button onClick={() => create.mutate()} disabled={!label.trim() || !file || create.isPending}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-40">
          {create.isPending ? 'Adding' : 'Add asset'}
        </button>
      </div>

      {isLoading ? (
        <div className="text-xs text-gray-500">Loading assets</div>
      ) : assets.length === 0 ? (
        <div className="text-xs text-gray-500">
          No brand assets yet. Until one is added, image generation behaves exactly as before.
        </div>
      ) : (
        <div className="space-y-2">
          {assets.map((a) => (
            <div key={a.id}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
              {a.url && <img src={a.url} alt="" className="h-8 w-8 object-contain rounded" />}
              <span className="text-xs font-medium flex-1 min-w-[120px]">{a.label}</span>
              <span className="text-[10px] text-gray-500">{a.usage_mode}</span>
              <label className="inline-flex items-center gap-1 text-[11px]">
                <input type="checkbox" checked={a.default_on}
                  onChange={(e) => patch.mutate({ id: a.id, body: { default_on: e.target.checked } })} />
                Attach by default
              </label>
              {a.usage_mode === 'overlay' && (
                <select value={a.overlay_position}
                  onChange={(e) => patch.mutate({ id: a.id, body: { overlay_position: e.target.value } })}
                  className="text-[11px] px-1.5 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent">
                  {POSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}
              {/* Overlay-only. A reference asset is guidance for the model, so a
                  width has no effect on it and showing the field implies it does. */}
              {a.usage_mode === 'overlay' && (
                <input type="number" min="1" max="100" value={a.overlay_width_pct}
                  onChange={(e) => patch.mutate({ id: a.id, body: { overlay_width_pct: Number(e.target.value) } })}
                  className="w-14 text-[11px] px-1.5 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent"
                  title="Width as a percentage of the image" />
              )}
              <button
                onClick={() => { if (window.confirm(`Remove "${a.label}"?`)) remove.mutate(a.id); }}
                className="text-orange-500 hover:text-orange-600" title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
